import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {{ name: string, sqm: number }} GesStand
 * @typedef {{
 *   enabled: boolean,
 *   mode?: 'hc' | 'stands',
 *   customStandValue: string,
 *   noneOfTheAboveLabel: string,
 *   stands: GesStand[],
 *   selected_sqm: number,
 *   base_variant_price: number,
 *   money_format: string,
 *   currency: string,
 *   wall_count: number,
 *   floor_rounding: number
 * }} GesStandPricingConfig
 */

/** @type {GesStandPricingConfig | null} */
let activeConfig = null;

/**
 * @returns {GesStandPricingConfig | null}
 */
function readConfig() {
  const node = document.getElementById('ges-stand-pricing-data');
  if (!node?.textContent) return null;

  try {
    const config = JSON.parse(node.textContent);
    return config?.enabled ? config : null;
  } catch {
    return null;
  }
}

/**
 * @param {number} cents
 * @returns {string}
 */
function formatPrice(cents) {
  const format = activeConfig?.money_format || '{{amount}}';
  const currency = activeConfig?.currency || '';
  return formatMoney(cents, format, currency);
}

/**
 * Round calculated sqm up to the next multiple of floor_rounding when set.
 * @param {number} sqm
 * @returns {number}
 */
function applyFloorRounding(sqm) {
  if (!sqm || sqm <= 0) return 0;

  const multiple = Number(activeConfig?.floor_rounding) || 0;
  if (multiple > 0) {
    return Math.ceil(sqm / multiple) * multiple;
  }

  return Math.round(sqm * 100) / 100;
}

/**
 * Booth floor area from wall lengths based on product sides configuration.
 * @param {number[]} walls
 * @param {number} wallCount
 * @returns {number}
 */
function calculateSqmFromWalls(walls, wallCount) {
  const values = walls
    .slice(0, wallCount)
    .map((w) => Math.max(0, Number(w) || 0));
  const [w1, w2, w3] = values;

  if (wallCount >= 3 && w1 > 0 && w2 > 0 && w3 > 0) {
    const length = (w1 + w3) / 2;
    return applyFloorRounding(length * w2);
  }

  if (wallCount >= 2 && w1 > 0 && w2 > 0) {
    return applyFloorRounding(w1 * w2);
  }

  if (wallCount == 1) {
    return applyFloorRounding(w1);
  }

  return 0;
}

/**
 * Floor stand area from width × length.
 * @param {number} width
 * @param {number} length
 * @returns {number}
 */
function calculateSqmFromFloor(width, length) {
  const w = Math.max(0, Number(width) || 0);
  const l = Math.max(0, Number(length) || 0);
  if (w <= 0 || l <= 0) return 0;
  return applyFloorRounding(w * l);
}

/**
 * @param {number} variantPriceCents
 * @param {number} [sqm]
 */
function updatePriceDisplays(variantPriceCents, sqm) {
  const calculatedCents = sqm ? variantPriceCents * sqm : variantPriceCents;
  const formatted = formatPrice(calculatedCents);

  document.querySelectorAll('product-price [ref="priceContainer"] .price').forEach((el) => {
    el.textContent = formatted;
  });

  document.querySelectorAll('.sticky-add-to-cart__price .price').forEach((el) => {
    el.textContent = formatted;
  });
}

/**
 * @param {HTMLElement} root
 * @returns {HTMLElement | null}
 */
function getRoot(root) {
  return root instanceof HTMLElement ? root : document.querySelector('[data-ges-stand-pricing]');
}

/**
 * @param {HTMLElement} root
 * @returns {boolean}
 */
function isHcMode(root) {
  return (
    activeConfig?.mode === 'hc' ||
    root?.dataset?.gesMode === 'hc'
  );
}

/**
 * @param {HTMLElement} root
 * @returns {boolean}
 */
function isCustomStandSelected(root) {
  if (isHcMode(root)) return true;

  const select = root.querySelector('[data-ges-stand-select]');
  if (!(select instanceof HTMLSelectElement) || !activeConfig) return false;

  const option = select.options[select.selectedIndex];
  return (
    select.value === activeConfig.customStandValue || option?.dataset.standCustom === 'true'
  );
}

const WALL_STEP_METERS = 0.5;

/**
 * @param {number} value
 * @returns {number}
 */
function snapToWallStep(value) {
  if (!value || value <= 0) return 0;
  return Math.round(value / WALL_STEP_METERS) * WALL_STEP_METERS;
}

/**
 * @param {HTMLElement} root
 * @param {boolean} isCustom
 */
function toggleCustomPanel(root, isCustom) {
  if (isHcMode(root)) return;

  const panel = root.querySelector('[data-ges-custom-stand-panel]');
  const wallInputs = root.querySelectorAll('[data-ges-wall-input]');
  const sqmNote = root.querySelector('[data-ges-calculated-sqm-note]');
  const requiresWallInput = (activeConfig?.wall_count || 0) > 0;

  if (panel instanceof HTMLElement) {
    panel.hidden = !isCustom;
  }

  if (sqmNote instanceof HTMLElement && !isCustom) {
    sqmNote.hidden = true;
    sqmNote.textContent = '';
  }

  wallInputs.forEach((input) => {
    if (input instanceof HTMLInputElement) {
      input.disabled = !isCustom;
      input.required = isCustom && requiresWallInput;
      if (!isCustom) {
        input.value = '';
      }
    }
  });
}

/**
 * @param {HTMLElement} root
 * @returns {number[]}
 */
function getWallValues(root) {
  return Array.from(root.querySelectorAll('[data-ges-wall-input]')).map((input) => {
    if (input instanceof HTMLInputElement) return Number(input.value) || 0;
    return 0;
  });
}

/**
 * @param {HTMLElement} root
 * @returns {{ width: number, length: number }}
 */
function getFloorValues(root) {
  const widthInput = root.querySelector('[data-ges-floor-dimension="width"]');
  const lengthInput = root.querySelector('[data-ges-floor-dimension="length"]');

  return {
    width: widthInput instanceof HTMLInputElement ? Number(widthInput.value) || 0 : 0,
    length: lengthInput instanceof HTMLInputElement ? Number(lengthInput.value) || 0 : 0,
  };
}

/**
 * @param {HTMLElement} root
 * @param {boolean} isCustom
 */
function setLineItemPropertyMode(root, isCustom) {
  const standInput = root.querySelector('[data-ges-stand-property="stand"]');
  const sqmInput = root.querySelector('[data-ges-stand-property="sqm"]');

  if (standInput instanceof HTMLInputElement) {
    if (isCustom) {
      standInput.removeAttribute('name');
      standInput.value = '';
      standInput.disabled = true;
    } else {
      const standName = standInput.dataset.gesStandPropertyName || 'properties[Stand]';
      standInput.setAttribute('name', standName);
      standInput.disabled = false;
    }
  }

  if (sqmInput instanceof HTMLInputElement) {
    const sqmName = isCustom
      ? sqmInput.dataset.gesSqmPropertyHidden || 'properties[_sqm]'
      : sqmInput.dataset.gesSqmPropertyVisible || 'properties[sqm]';
    sqmInput.setAttribute('name', sqmName);
  }
}

/**
 * @param {HTMLElement} root
 * @param {{ isCustom: boolean, standName?: string, sqm: number }} options
 */
function updateLineItemProperties(root, { isCustom, standName, sqm }) {
  setLineItemPropertyMode(root, isCustom);

  const sqmInput = root.querySelector('[data-ges-stand-property="sqm"]');
  const standInput = root.querySelector('[data-ges-stand-property="stand"]');

  if (sqmInput instanceof HTMLInputElement) sqmInput.value = String(sqm);

  if (!isCustom && standInput instanceof HTMLInputElement && standName) {
    standInput.value = standName;
  }
}

/**
 * @param {HTMLElement} root
 * @param {number} sqm
 */
function updateCalculatedSqmNote(root, sqm) {
  const note = root.querySelector('[data-ges-calculated-sqm-note]');
  if (!(note instanceof HTMLElement)) return;

  if (sqm > 0) {
    const template = root.dataset.calculatedSqmTemplate || 'Calculated area: __sqm__ sqm';
    note.textContent = template.replace('__sqm__', String(sqm));
    note.hidden = false;
  } else {
    note.textContent = '';
    note.hidden = true;
  }
}

/**
 * @param {HTMLOptionElement} option
 * @returns {{ name: string, sqm: number }}
 */
function standFromOption(option) {
  return {
    name: option.dataset.standName || option.textContent?.split(' (')[0] || '',
    sqm: Number(option.dataset.standSqm) || 0,
  };
}

/**
 * @param {GesStandPricingConfig} config
 * @param {number} variantPriceCents
 */
function initHcStandPricing(config, variantPriceCents) {
  const root = getRoot(document.body);
  if (!root) return;

  let currentSqm = 0;

  const applyFloorStand = () => {
    const { width, length } = getFloorValues(root);
    const sqm = calculateSqmFromFloor(width, length);
    currentSqm = sqm;
    updateLineItemProperties(root, { isCustom: true, sqm });
    updatePriceDisplays(variantPriceCents, sqm);
    updateCalculatedSqmNote(root, sqm);
  };

  root.querySelectorAll('[data-ges-floor-input]').forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;

    const snapFloorValue = () => {
      const snapped = snapToWallStep(Number(input.value));
      input.value = snapped > 0 ? String(snapped) : '';
      applyFloorStand();
    };

    input.addEventListener('change', snapFloorValue);
    input.addEventListener('blur', snapFloorValue);
    input.addEventListener('input', applyFloorStand);
  });

  const productForm = document.querySelector('form[data-type="add-to-cart-form"]');
  productForm?.addEventListener(
    'submit',
    (event) => {
      const { width, length } = getFloorValues(root);
      const sqm = calculateSqmFromFloor(width, length);

      if (sqm <= 0) {
        event.preventDefault();
        const firstFloor = root.querySelector('[data-ges-floor-input]');
        if (firstFloor instanceof HTMLInputElement) firstFloor.focus();
      }
    },
    { capture: true }
  );

  applyFloorStand();

  document.querySelectorAll('.shopify-section').forEach((section) => {
    section.addEventListener(ThemeEvents.variantUpdate, (event) => {
      if (!(event instanceof VariantUpdateEvent)) return;

      const variant = event.detail.resource;
      if (!variant?.price) return;

      variantPriceCents = variant.price;
      updatePriceDisplays(variantPriceCents, currentSqm);
    });
  });
}

/**
 * @param {GesStandPricingConfig} config
 * @param {number} variantPriceCents
 */
function initStandPricing(config, variantPriceCents) {
  const root = getRoot(document.body);
  if (!root) return;

  if (isHcMode(root)) {
    initHcStandPricing(config, variantPriceCents);
    return;
  }

  const select = root.querySelector('[data-ges-stand-select]');
  let currentSqm = config.selected_sqm || config.stands[0]?.sqm || 0;

  const applyPredefinedStand = (stand) => {
    if (!stand?.sqm) return;
    currentSqm = stand.sqm;
    toggleCustomPanel(root, false);
    updateLineItemProperties(root, { isCustom: false, standName: stand.name, sqm: currentSqm });
    updatePriceDisplays(variantPriceCents, currentSqm);
    updateCalculatedSqmNote(root, 0);
  };

  const applyCustomStand = () => {
    const wallCount = config.wall_count || 0;
    const walls = getWallValues(root);
    const sqm = calculateSqmFromWalls(walls, wallCount);
    currentSqm = sqm;
    toggleCustomPanel(root, true);
    updateLineItemProperties(root, { isCustom: true, sqm });
    updatePriceDisplays(variantPriceCents, sqm);
    updateCalculatedSqmNote(root, sqm);
  };

  const handleSelectionChange = () => {
    if (isCustomStandSelected(root)) {
      applyCustomStand();
    } else if (select instanceof HTMLSelectElement) {
      const option = select.options[select.selectedIndex];
      if (option) applyPredefinedStand(standFromOption(option));
    }
  };

  if (select instanceof HTMLSelectElement) {
    select.addEventListener('change', handleSelectionChange);
  }

  root.querySelectorAll('[data-ges-wall-input]').forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;

    const snapWallValue = () => {
      if (!isCustomStandSelected(root)) return;
      const snapped = snapToWallStep(Number(input.value));
      input.value = snapped > 0 ? String(snapped) : '';
      applyCustomStand();
    };

    input.addEventListener('change', snapWallValue);
    input.addEventListener('blur', snapWallValue);
  });

  const productForm = document.querySelector('form[data-type="add-to-cart-form"]');
  productForm?.addEventListener(
    'submit',
    (event) => {
      if (!isCustomStandSelected(root)) return;

      const wallCount = config.wall_count || 0;
      if (wallCount <= 0) return;

      const walls = getWallValues(root);
      const sqm = calculateSqmFromWalls(walls, wallCount);

      if (sqm <= 0) {
        event.preventDefault();
        const firstWall = root.querySelector('[data-ges-wall-input]');
        if (firstWall instanceof HTMLInputElement) firstWall.focus();
      }
    },
    { capture: true }
  );

  handleSelectionChange();

  document.querySelectorAll('.shopify-section').forEach((section) => {
    section.addEventListener(ThemeEvents.variantUpdate, (event) => {
      if (!(event instanceof VariantUpdateEvent)) return;

      const variant = event.detail.resource;
      if (!variant?.price) return;

      variantPriceCents = variant.price;
      updatePriceDisplays(variantPriceCents, currentSqm);
    });
  });
}

function bootstrap() {
  const config = readConfig();
  if (!config) return;

  activeConfig = config;

  const variantPicker = document.querySelector('variant-picker script[type="application/json"]');
  let variantPrice = config.base_variant_price;

  if (variantPicker?.textContent) {
    try {
      const variant = JSON.parse(variantPicker.textContent);
      if (variant?.price) variantPrice = variant.price;
    } catch {
      // use config fallback
    }
  }

  initStandPricing(config, variantPrice);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}
